import { zodResolver } from "@hookform/resolvers/zod";
import React from "react";
import { Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { z } from "zod";

import AreaBuilderNumberFormGroup from "../../components/FormGroups/AreaBuilderFormGroups.jsx";
import SelectFormGroup from "../../components/FormGroups/SelectFormGroup.jsx";
import TextFormGroup from "../../components/FormGroups/TextFormGroup.jsx";
import { numberFromString } from "../../utils/zodUtils.js";

function alphaToNumber(input) {
    if (!/^[A-Za-z]+$/.test(input)) return input;
    const formattedInput = input.trim().toUpperCase();
    let number = 0;
    for (let i = 0; i < formattedInput.length; i++) {
        number = number * 26 + (formattedInput.charCodeAt(i) - 64);
    }
    return number - 1;
}

const gridPartSchema = z.object({
    amount: numberFromString.positive(),
    size: numberFromString,

    //OFFSETS
    startOffset: numberFromString,
    spacing: numberFromString,
    skewness: numberFromString,

    // NAMER
    scheme: z.string(),
    capitalization: z.string(),
    initial: z.preprocess((input) => alphaToNumber(input), numberFromString),
    stepSize: numberFromString,
    order: z.string(),
    prefix: z.string(),
    suffix: z.string(),
    zeroPad: numberFromString,
});

export default function AreaSettingsCard({ defaults, onChange }) {
    const areaConfigSchema = z.object({
        columnParams: gridPartSchema,
        rowParams: gridPartSchema,

        z: numberFromString,
        id: z.string(),
    });

    const {
        control,
        watch,
        setValue,
        formState: { errors },
    } = useForm({
        resolver: zodResolver(areaConfigSchema),
        defaultValues: defaults,
    });

    const watchedValues = watch();
    const lastEmittedRef = React.useRef(JSON.stringify(defaults));

    React.useEffect(() => {
        const parsed = areaConfigSchema.safeParse(watchedValues);
        if (!parsed.success) return;

        const dataStr = JSON.stringify(parsed.data);

        if (dataStr !== lastEmittedRef.current) {
            lastEmittedRef.current = dataStr;
            onChange(parsed.data);
        }
    }, [watchedValues, onChange, areaConfigSchema]);

    return (
        <Form className="px-3">
            <AreaBuilderNumberFormGroup
                label="Width"
                name="columnParams.size"
                control={control}
                errors={errors}
                setValue={setValue}
                unitText="m"
                step={0.01}
            />
            <AreaBuilderNumberFormGroup
                label="Height"
                name="rowParams.size"
                control={control}
                errors={errors}
                setValue={setValue}
                unitText="m"
                step={0.01}
            />
            <AreaBuilderNumberFormGroup
                label="Number of Rows"
                name="rowParams.amount"
                control={control}
                errors={errors}
                setValue={setValue}
            />
            <AreaBuilderNumberFormGroup
                label="Number of Columns"
                name="columnParams.amount"
                control={control}
                errors={errors}
                setValue={setValue}
            />
            <AreaBuilderNumberFormGroup
                label="Z"
                name="z"
                control={control}
                errors={errors}
                setValue={setValue}
                unitText="m"
                step={0.01}
            />

            <h4>Offsets</h4>
            <AreaBuilderNumberFormGroup
                label="Column Start Offset"
                name="columnParams.startOffset"
                control={control}
                errors={errors}
                setValue={setValue}
                unitText="m"
                step={0.01}
            />
            <AreaBuilderNumberFormGroup
                label="Column Spacing"
                name="columnParams.spacing"
                control={control}
                errors={errors}
                setValue={setValue}
                unitText="m"
                step={0.01}
            />
            <AreaBuilderNumberFormGroup
                label="Column skewness"
                name="columnParams.skewness"
                control={control}
                errors={errors}
                setValue={setValue}
                unitText="m"
                step={0.01}
            />
            <AreaBuilderNumberFormGroup
                label="Row Start Offset"
                name="rowParams.startOffset"
                control={control}
                errors={errors}
                setValue={setValue}
                unitText="m"
                step={0.01}
            />
            <AreaBuilderNumberFormGroup
                label="Row Spacing"
                name="rowParams.spacing"
                control={control}
                errors={errors}
                setValue={setValue}
                unitText="m"
                step={0.01}
            />
            <AreaBuilderNumberFormGroup
                label="Row skewness"
                name="rowParams.skewness"
                control={control}
                errors={errors}
                setValue={setValue}
                unitText="m"
                step={0.01}
            />

            <h4>Row Name</h4>
            <SelectFormGroup
                label="Order"
                name="rowParams.order"
                control={control}
                errors={errors}
                setValue={setValue}
                options={[
                    { id: "Ascending", name: "Ascending" },
                    { id: "Descending", name: "Descending" },
                ]}
                distribution={[6, 6]}
            />
            <SelectFormGroup
                label="Scheme"
                name="rowParams.scheme"
                control={control}
                errors={errors}
                setValue={setValue}
                options={[
                    { id: "numeric", name: "Numeric" },
                    { id: "alphabetical", name: "Alphabetical" },
                ]}
                distribution={[6, 6]}
            />
            <TextFormGroup
                label="Prefix"
                name="rowParams.prefix"
                control={control}
                errors={errors}
                setValue={setValue}
                distribution={[6, 6]}
            />
            <TextFormGroup
                label="Suffix"
                name="rowParams.suffix"
                control={control}
                errors={errors}
                setValue={setValue}
                distribution={[6, 6]}
            />
            {watchedValues.rowParams.scheme === "alphabetical" && (
                <SelectFormGroup
                    label="Capitalization"
                    name="rowParams.capitalization"
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    options={[
                        { id: "lowercase", name: "Lowercase" },
                        { id: "uppercase", name: "Uppercase" },
                    ]}
                    distribution={[6, 6]}
                />
            )}
            <AreaBuilderNumberFormGroup
                label="Offset"
                name="rowParams.initial"
                control={control}
                errors={errors}
                setValue={setValue}
                distribution={[6, 6]}
                canBeText
            />
            <AreaBuilderNumberFormGroup
                label="Step Size"
                name="rowParams.stepSize"
                control={control}
                errors={errors}
                setValue={setValue}
                distribution={[6, 6]}
            />
            {watchedValues.rowParams.scheme === "numeric" && (
                <AreaBuilderNumberFormGroup
                    label="Zero padding"
                    name="rowParams.zeroPad"
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    distribution={[6, 6]}
                />
            )}

            <h4>Column Name</h4>
            <SelectFormGroup
                label="Order"
                name="columnParams.order"
                control={control}
                errors={errors}
                setValue={setValue}
                options={[
                    { id: "Ascending", name: "Ascending" },
                    { id: "Descending", name: "Descending" },
                ]}
                distribution={[6, 6]}
            />
            <SelectFormGroup
                label="Scheme"
                name="columnParams.scheme"
                control={control}
                errors={errors}
                setValue={setValue}
                options={[
                    { id: "numeric", name: "Numeric" },
                    { id: "alphabetical", name: "Alphabetical" },
                ]}
                distribution={[6, 6]}
            />
            <TextFormGroup
                label="Prefix"
                name="columnParams.prefix"
                control={control}
                errors={errors}
                setValue={setValue}
                distribution={[6, 6]}
            />
            <TextFormGroup
                label="Suffix"
                name="columnParams.suffix"
                control={control}
                errors={errors}
                setValue={setValue}
                distribution={[6, 6]}
            />
            {watchedValues.columnParams.scheme === "alphabetical" && (
                <SelectFormGroup
                    label="Capitalization"
                    name="columnParams.capitalization"
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    options={[
                        { id: "lowercase", name: "Lowercase" },
                        { id: "uppercase", name: "Uppercase" },
                    ]}
                    distribution={[6, 6]}
                />
            )}
            <AreaBuilderNumberFormGroup
                label="Offset"
                name="columnParams.initial"
                control={control}
                errors={errors}
                setValue={setValue}
                distribution={[6, 6]}
                canBeText
            />
            <AreaBuilderNumberFormGroup
                label="Step Size"
                name="columnParams.stepSize"
                control={control}
                errors={errors}
                setValue={setValue}
                distribution={[6, 6]}
            />
            {watchedValues.columnParams.scheme === "numeric" && (
                <AreaBuilderNumberFormGroup
                    label="Zero padding"
                    name="columnParams.zeroPad"
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    distribution={[6, 6]}
                />
            )}
        </Form>
    );
}
